import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Wifi, MapPin, Zap, Shield, ArrowRight, CheckCircle, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const features = [
  {
    icon: Wifi,
    title: 'Teknologji NFC',
    description: 'Stikera të vegjël që vendosen lehtësisht në çdo objekt të vlefshëm',
    gradient: 'from-violet-500 to-purple-600'
  },
  {
    icon: MapPin,
    title: 'Lokacion i Saktë',
    description: 'Merrni koordinatat e sakta në Google Maps kur dikush skanon stikerin',
    gradient: 'from-cyan-500 to-blue-600'
  },
  {
    icon: Zap,
    title: 'Njoftime Instant',
    description: 'Merrni njoftim sapo dikush të gjejë objektin tuaj të humbur',
    gradient: 'from-amber-500 to-orange-600'
  },
  {
    icon: Shield,
    title: 'Privaci e Sigurt',
    description: 'Informacioni juaj personal mbetet i mbrojtur dhe konfidencial',
    gradient: 'from-emerald-500 to-teal-600'
  }
];

const howItWorks = [
  { step: '01', title: 'Regjistrohu', description: 'Krijo një llogari falas dhe blej stikerët NFC' },
  { step: '02', title: 'Lidh Stikerin', description: 'Shto objektet e tua dhe lidh kodin e stikerit' },
  { step: '03', title: 'Merrni Njoftim', description: 'Kur dikush skanon stikerin, merrni lokacionin menjëherë' }
];

const LandingPage = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50 overflow-hidden">
      {/* Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="floating-orb w-96 h-96 bg-primary/30 -top-48 -left-48 animate-float" style={{ animationDelay: '0s' }} />
        <div className="floating-orb w-80 h-80 bg-cyan-500/20 top-1/3 -right-40 animate-float" style={{ animationDelay: '2s' }} />
        <div className="floating-orb w-64 h-64 bg-emerald-500/20 bottom-20 left-1/4 animate-float" style={{ animationDelay: '4s' }} />
        <div className="absolute inset-0 grid-pattern opacity-50" />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
        <div className="container-app">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG" 
                alt="TAPTAG Logo"
                className="w-10 h-10 rounded-full object-cover shadow-lg"
              />
              <span className="text-xl font-bold text-slate-900">TapTag</span>
            </Link>
            
            <div className="flex items-center gap-2">
              <Link to="/login" className="text-xs sm:text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors px-2 sm:px-3 py-2" data-testid="nav-login">
                Hyr
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="gradient-primary text-white rounded-full px-3 sm:px-5 h-8 sm:h-9 text-xs sm:text-sm font-semibold shadow-lg hover:opacity-90 transition-all">
                  Regjistrohu
                </Button>
              </Link>
              <Link to="/shop" data-testid="nav-shop">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-3 sm:px-5 h-8 sm:h-9 text-xs sm:text-sm font-semibold">
                  SHOP
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-4 relative">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-5 py-2.5 rounded-full text-sm font-semibold animate-fade-in-up border border-primary/20">
                <Wifi className="w-4 h-4 animate-pulse" />
                Teknologji NFC e Avancuar
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 leading-[1.1] animate-fade-in-up" style={{ animationDelay: '0.1s' }} data-testid="hero-title">
                Riktheni objektet e
                <span className="bg-gradient-to-r from-primary via-purple-500 to-cyan-500 bg-clip-text text-transparent animate-gradient"> humbura</span>
              </h1>
              
              <p className="text-xl text-slate-600 max-w-lg leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                TapTag përdor teknologjinë NFC për të lidhur objektet tuaja të vlefshme me ju. Një skanim i thjeshtë dhe ju merrni lokacionin e saktë.
              </p>
              
              <div className="flex flex-wrap gap-4 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <Link to={isAuthenticated ? "/llogaria" : "/register"} data-testid="hero-register-btn">
                  <Button className="gradient-primary text-white rounded-full px-10 h-16 text-lg font-bold shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 group">
                    Fillo Tani
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-login-btn">
                  <Button variant="outline" className="rounded-full px-10 h-16 text-lg font-semibold border-2 hover:bg-slate-100 hover:scale-105 transition-all duration-300">
                    Kam llogari
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center gap-8 pt-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="font-medium">Falas për t'u filluar</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <span className="font-medium">100% Privaci</span>
                </div>
              </div>
            </div>
            
            {/* Right Content - Image */}
            <div className="relative animate-fade-in-right" style={{ animationDelay: '0.3s' }}>
              <div className="absolute inset-0 gradient-primary rounded-3xl blur-3xl opacity-20 animate-pulse" />
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl hover-lift">
                <img 
                  src="https://images.pexels.com/photos/5448160/pexels-photo-5448160.jpeg" 
                  alt="Smartphone with location tracking"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-5 animate-fade-in-up">
              Si funksionon <span className="bg-gradient-to-r from-primary to-cyan-500 bg-clip-text text-transparent">TapTag</span>?
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Zgjidhje e thjeshtë dhe efektive për mbrojtjen e objekteve tuaja të vlefshme
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {features.map((feature, index) => (
              <Card key={index} className="tech-card border-0 overflow-hidden group cursor-pointer" data-testid={`feature-card-${index}`}>
                <CardContent className="p-7 space-y-5">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    <feature.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-gradient-to-b from-slate-100 to-white relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-50" />
        <div className="container-app relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-5 animate-fade-in-up">
              3 Hapa të <span className="bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">Thjeshtë</span>
            </h2>
            <p className="text-xl text-slate-600 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              Filloni të mbroni objektet tuaja në vetëm disa minuta
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative animate-fade-in-up" data-testid={`step-card-${index}`} style={{ animationDelay: `${0.2 + index * 0.1}s` }}>
                <div className="text-8xl font-extrabold text-primary/10 absolute -top-4 -left-2">
                  {item.step}
                </div>
                <div className="relative z-10 pt-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600 text-lg">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-mesh" />
        <div className="absolute inset-0 grid-pattern opacity-10" />
        <div className="container-app relative z-10 text-center">
          <div className="animate-fade-in-up">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 text-glow">
              Gati për të ruajtur objektet tuaja?
            </h2>
            <p className="text-white/80 text-xl mb-10 max-w-xl mx-auto">
              Bashkohuni me mijëra përdorues që kanë rikthyer objektet e tyre të humbura me TapTag
            </p>
            <Link to="/register" data-testid="cta-register-btn">
              <Button className="rounded-full px-12 h-16 text-lg font-bold bg-white text-slate-900 hover:bg-slate-100 shadow-2xl hover:scale-105 transition-all duration-300 group">
                Krijo Llogari Falas
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          
          {/* Floating dots */}
          <div className="absolute top-10 left-10 w-4 h-4 bg-white/20 rounded-full animate-float" style={{ animationDelay: '0s' }} />
          <div className="absolute top-20 right-20 w-3 h-3 bg-cyan-400/30 rounded-full animate-float" style={{ animationDelay: '1s' }} />
          <div className="absolute bottom-20 left-1/4 w-5 h-5 bg-purple-400/20 rounded-full animate-float" style={{ animationDelay: '2s' }} />
          <div className="absolute bottom-10 right-1/3 w-4 h-4 bg-emerald-400/20 rounded-full animate-float" style={{ animationDelay: '3s' }} />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_726f4ba1-c975-4f39-ba02-ad3131a12031/artifacts/hgye1jst_IMG_0895%20%281%29.PNG" 
                alt="TAPTAG Logo"
                className="w-8 h-8 rounded-full object-cover"
              />
              <span className="text-sm text-slate-400">TAPTAG © 2024</span>
            </div>
            <div className="flex items-center gap-6">
              <Link to="/shop" className="text-sm text-slate-400 hover:text-white transition-colors">
                Shop
              </Link>
              <a href="mailto:support@taptag.al" className="text-sm text-slate-400 hover:text-white transition-colors">
                Kontakt
              </a>
              <Link to="/admin" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1" data-testid="admin-link">
                <Settings className="w-4 h-4" />
                Admin Panel
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
